import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { withReauth } from '@/lib/auth';

/**
 * Global scope storage for tenant and organization slugs
 * Updated by ScopeHeadersProvider or from JWT claims
 */
let globalScope: {
  tenantSlug: string;
  organizationSlug: string | undefined;
} = {
  tenantSlug: 'ptf', // Default tenant
  organizationSlug: undefined,
};

/**
 * Update global scope from ScopeHeadersProvider or auth state
 * Called when URL params change or user authenticates
 */
export function updateGlobalScope(
  tenantSlug: string,
  organizationSlug: string | undefined,
) {
  globalScope = { tenantSlug, organizationSlug };
}

/**
 * Custom base query with scope headers, JWT auth, and automatic token refresh on 401.
 */
export const TENANT_API_BASE_URL =
  import.meta.env.VITE_TENANT_API_URL || 'http://localhost:3012';

const baseQueryWithScope = withReauth((accessToken) =>
  fetchBaseQuery({
    baseUrl: TENANT_API_BASE_URL,
    prepareHeaders: (headers) => {
      headers.set('x-tenant-slug', globalScope.tenantSlug);
      if (globalScope.organizationSlug) {
        headers.set('x-organization-slug', globalScope.organizationSlug);
      }

      if (accessToken) {
        headers.set('Authorization', `Bearer ${accessToken}`);
      }

      return headers;
    },
  }),
);

export const tenantApi = createApi({
  reducerPath: 'tenantApi',
  baseQuery: baseQueryWithScope,

  // ⭐ Global tag types for cache invalidation (tenant-api resources)
  tagTypes: [
    // Applications
    'Application',
    'Submission',
    'AdditionalFile',
    'Permit',
    'ApplicationResource',
    'ApplicableChangeset',
    // Country Configs
    'CountryConfig',
    'CountryConfigCustomization',
    'CountryConfigScope',
    'CountryConfigFir',
    'Fir',
    'Airport',
    'Scope',
    'PermitConfig',
    'PermitConfigCustomization',
    'DocumentType',
    'EventLog',
    // Organizations
    'Organization',
    'TenantProfile',
    // Fleet
    'Aircraft',
    'IcaoType',
    // Users
    'User',
    'Role',
    // Flights
    'Flight',
    'FlightGroup',
    'Route',
    'CityPair',
    // Crew & Passengers
    'Crew',
    'Pax',
    // Cargo & Charterers
    'Cargo',
    'CargoOrganization',
    'Charterer',
    // Station Managers
    'StationManager',
    'StationManagerCountry',
    'StationManagerCountryAirport',
    // Documents
    'Document',
    'FlightDocument',
    'FlightFieldData',
    'SpecificData',
    // Tasks
    'Task',
    'TaskLabel',
    // Settings & Operations
    'Settings',
    'BatchOperation',
    'Changeset',
    // Comments
    'Comment',
    // Service Providers
    'ServiceProvider',
    'ServiceType',
    'ServiceTypeSettings',
    'DefaultDataSettings',
    // Analytics
    'CustomEventType',
    // Dashboard
    'Dashboard',
    // Notes
    'Note',
    // Permissions
    'Permission',
    // Integrations
    'IntegrationCategories',
    'TenantIntegrations',
    // Labels (polymorphic)
    'FlightLabel',
    // Notifications
    'Notification',
  ],

  // ⭐ Cache configuration for fast navigation
  keepUnusedDataFor: 300, // Keep cache for 5 minutes
  refetchOnMountOrArgChange: false, // Use cache on navigation, don't refetch
  refetchOnFocus: false, // Don't refetch on window focus
  refetchOnReconnect: true, // Refetch when connection restored

  // ⭐ Features will inject their endpoints here
  endpoints: () => ({}),
});

// Export base hooks (usually not used directly, but available)
export const {} = tenantApi;
